import { BaseTool } from './BaseTool';
import { ToolType } from '../types';

export class SmudgeTool extends BaseTool {
  public id: ToolType = 'smudge';
  public name = 'Smudge Tool';
  public cursor = 'none';

  private isSmudging = false;
  private lastX = 0;
  private lastY = 0;
  private currentMouseX = 0;
  private currentMouseY = 0;
  private smudgeBuffer: ImageData | null = null;
  private bufferSize = 0;

  public onMouseDown(e: MouseEvent, canvasX: number, canvasY: number): void {
    const layer = this.editor.getActiveLayer();
    if (!layer || layer.locked || !layer.visible) return;

    this.isSmudging = true;
    this.lastX = canvasX - layer.x;
    this.lastY = canvasY - layer.y;

    const radius = Math.round(this.editor.toolSettings.smudgeRadius);
    this.bufferSize = radius * 2;

    // Pick up initial color patch under brush
    const bx = Math.round(this.lastX - radius);
    const by = Math.round(this.lastY - radius);

    this.smudgeBuffer = this.capturePatch(layer, bx, by, this.bufferSize);
  }

  public onMouseMove(e: MouseEvent, canvasX: number, canvasY: number): void {
    this.currentMouseX = canvasX;
    this.currentMouseY = canvasY;

    if (!this.isSmudging) {
      this.editor.renderOverlayOnly();
      return;
    }

    const layer = this.editor.getActiveLayer();
    if (!layer || layer.locked || !layer.visible || !this.smudgeBuffer) return;

    const lx = canvasX - layer.x;
    const ly = canvasY - layer.y;

    const dist = Math.hypot(lx - this.lastX, ly - this.lastY);
    const stepSize = Math.max(1, this.editor.toolSettings.smudgeRadius * 0.2);
    const steps = Math.ceil(dist / stepSize);

    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const curX = this.lastX + (lx - this.lastX) * t;
      const curY = this.lastY + (ly - this.lastY) * t;
      this.applySmudgeStep(layer, curX, curY);
    }

    this.lastX = lx;
    this.lastY = ly;
    this.editor.render();
  }

  public onMouseUp(_e: MouseEvent): void {
    if (this.isSmudging) {
      this.isSmudging = false;
      this.smudgeBuffer = null;
      this.editor.recordHistory('Smudge');
    }
  }

  private capturePatch(layer: any, x: number, y: number, size: number): ImageData {
    const patch = layer.ctx.createImageData(size, size);
    const srcData = layer.ctx.getImageData(
      Math.max(0, x),
      Math.max(0, y),
      Math.min(layer.width - Math.max(0, x), size),
      Math.min(layer.height - Math.max(0, y), size)
    );

    // Copy into patch data
    const ox = Math.max(0, -x);
    const oy = Math.max(0, -y);
    for (let py = 0; py < srcData.height; py++) {
      for (let px = 0; px < srcData.width; px++) {
        const sIdx = (py * srcData.width + px) * 4;
        const dIdx = ((py + oy) * size + (px + ox)) * 4;
        patch.data[dIdx] = srcData.data[sIdx];
        patch.data[dIdx + 1] = srcData.data[sIdx + 1];
        patch.data[dIdx + 2] = srcData.data[sIdx + 2];
        patch.data[dIdx + 3] = srcData.data[sIdx + 3];
      }
    }
    return patch;
  }

  private applySmudgeStep(layer: any, centerX: number, centerY: number) {
    if (!this.smudgeBuffer) return;

    const radius = Math.round(this.editor.toolSettings.smudgeRadius);
    const size = radius * 2;
    const strength = this.editor.toolSettings.smudgeStrength / 100; // 0 to 1

    const bx = Math.round(centerX - radius);
    const by = Math.round(centerY - radius);

    // Read current destination pixels
    const dest = this.capturePatch(layer, bx, by, size);
    const buf = this.smudgeBuffer.data;
    const dst = dest.data;

    // Blend smudge buffer into dest with radial falloff
    for (let py = 0; py < size; py++) {
      for (let px = 0; px < size; px++) {
        const dx = px - radius;
        const dy = py - radius;
        const d = Math.hypot(dx, dy);
        if (d > radius) continue;

        // Radial falloff: full in center, smooth 0 at radius edge
        const falloff = (1 - d / radius);
        const factor = strength * falloff;

        const idx = (py * size + px) * 4;

        if (buf[idx + 3] > 0 || dst[idx + 3] > 0) {
          // Blend destination towards buffer
          dst[idx] = Math.round(dst[idx] * (1 - factor) + buf[idx] * factor);
          dst[idx + 1] = Math.round(dst[idx + 1] * (1 - factor) + buf[idx + 1] * factor);
          dst[idx + 2] = Math.round(dst[idx + 2] * (1 - factor) + buf[idx + 2] * factor);
          dst[idx + 3] = Math.round(dst[idx + 3] * (1 - factor) + buf[idx + 3] * factor);

          // Update smudge buffer so it picks up some underlying canvas color (like real smudge)
          const pickupRate = 0.25;
          buf[idx] = Math.round(buf[idx] * (1 - pickupRate) + dst[idx] * pickupRate);
          buf[idx + 1] = Math.round(buf[idx + 1] * (1 - pickupRate) + dst[idx + 1] * pickupRate);
          buf[idx + 2] = Math.round(buf[idx + 2] * (1 - pickupRate) + dst[idx + 2] * pickupRate);
          buf[idx + 3] = Math.round(buf[idx + 3] * (1 - pickupRate) + dst[idx + 3] * pickupRate);
        }
      }
    }

    // Write blended pixels back to layer canvas
    // Create temporary canvas to draw clipped circular patch
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = size;
    tempCanvas.height = size;
    const tCtx = tempCanvas.getContext('2d');
    if (tCtx) {
      tCtx.putImageData(dest, 0, 0);
      layer.ctx.drawImage(tempCanvas, bx, by);
    }
  }

  public renderOverlay(ctx: CanvasRenderingContext2D): void {
    const zoom = this.editor.zoom;
    const panX = this.editor.panX;
    const panY = this.editor.panY;

    const sx = this.currentMouseX * zoom + panX;
    const sy = this.currentMouseY * zoom + panY;
    const radius = this.editor.toolSettings.smudgeRadius * zoom;

    ctx.save();
    ctx.beginPath();
    ctx.arc(sx, sy, radius, 0, Math.PI * 2);
    ctx.strokeStyle = '#a855f7';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();
  }
}
